import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money.js';
import { HeroNumber } from '../../../components/ui/HeroNumber.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { useToast } from '../../../components/ui/Toast.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { Search, Receipt, Calendar, ArrowUpRight, Clock, Play } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { formatMoney } from '@repo/utils';
import { usePageTitle } from '../../../lib/hooks.js';
import {
  useSchemes,
  useScheme,
  useSchemeCycles,
  useSchemeSubscribers,
  useCycleSummary,
  useCycleDues,
  useSchemeReceipts,
  type DueRow,
  type DueStatus,
} from '../../../features/schemes/network/index.js';
import type { SchemeDetail } from '../../../features/schemes/network';

type SchemeExtra = SchemeDetail & {
  displayName?: string;
  schemeNumber?: string;
  durationCycles?: number;
};
import { RecordPaymentModal } from '../../../features/schemes/components/RecordPaymentModal.js';
import { CancelReceiptModal } from '../../../features/schemes/components/CancelReceiptModal.js';

export const Route = createFileRoute('/_auth/finance/receipts')({
  component: ChitReceiptsPage,
});

const DUES_STATUS_FILTERS = [
  { value: '', label: 'All Dues' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'PAID', label: 'Paid' },
];

function dueStatusVariant(status: DueStatus): 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'PAID': return 'success';
    case 'PARTIAL': return 'warning';
    case 'OVERDUE': return 'danger';
    default: return 'info';
  }
}

function isSameDayReceipt(collectedAt: string): boolean {
  const today = new Date();
  const collected = new Date(collectedAt);
  return (
    today.getUTCFullYear() === collected.getUTCFullYear() &&
    today.getUTCMonth() === collected.getUTCMonth() &&
    today.getUTCDate() === collected.getUTCDate()
  );
}

function ChitReceiptsPage() {
  usePageTitle('Chit Receipts');
  const { toast } = useToast();

  const [schemeId, setSchemeId] = React.useState<string>('');
  const [cycleNum, setCycleNum] = React.useState<number>(1);
  const [receiptPage, setReceiptPage] = React.useState<number>(1);
  const [duesPage, setDuesPage] = React.useState<number>(1);
  const [activeTab, setActiveTab] = React.useState<'receipts' | 'dues'>('receipts');
  const [duesStatusFilter, setDuesStatusFilter] = React.useState<string>('');

  const [recordPaymentDue, setRecordPaymentDue] = React.useState<DueRow | null>(null);
  const [cancelTarget, setCancelTarget] = React.useState<{
    receiptId: string;
    receiptNumber: string;
    isSameDay: boolean;
  } | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string>('');

  const { data: schemesResponse, isLoading: schemesLoading } = useSchemes({ limit: 100 });
  const schemesList = schemesResponse?.data ?? [];

  const { data: schemeDetailRaw } = useScheme(schemeId);
  const schemeDetail = schemeDetailRaw as SchemeExtra | undefined;

  React.useEffect(() => {
    if (schemeDetail?.currentCycle) {
      setCycleNum(schemeDetail.currentCycle);
    }
  }, [schemeDetail]);

  const { data: cyclesList } = useSchemeCycles(schemeId);
  const cycleNumberMap = React.useMemo(() => {
    if (!cyclesList) return new Map<string, number>();
    return new Map(cyclesList.map((c) => [c.id, c.cycleNumber]));
  }, [cyclesList]);

  const { data: subscribersResponse } = useSchemeSubscribers(schemeId, { limit: 100 });
  const subscribersList = subscribersResponse?.data || [];
  const subscriberMap = React.useMemo(() => {
    return new Map(
      subscribersList.map((s) => [
        s.id,
        { name: s.customerName || `Subscriber ${s.id.slice(0, 8)}`, tickets: s.ticketNumbers?.length ? `Ticket ${s.ticketNumbers.join(', ')}` : '' },
      ]),
    );
  }, [subscribersList]);

  const { data: receiptsResponse, isLoading: receiptsLoading, error: receiptsError, refetch: refetchReceipts } = useSchemeReceipts(schemeId, receiptPage);
  const receiptsData = receiptsResponse?.data || [];
  const receiptsTotal = receiptsResponse?.meta?.total || 0;

  const { data: duesResponse, isLoading: duesLoading, error: duesError, refetch: refetchDues } = useCycleDues(schemeId, cycleNum, duesPage, duesStatusFilter || undefined);
  const duesData = duesResponse?.data || [];
  const duesTotal = duesResponse?.meta?.total || 0;

  const { data: summary, isLoading: summaryLoading } = useCycleSummary(schemeId, cycleNum);

  const toolbar = (
    <div className="flex flex-wrap gap-4 items-center justify-between w-full md:w-auto bg-surface-1 border border-border rounded-xl px-4 py-3 shadow-sm">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex flex-col gap-1">
          <label className="text-caption font-bold text-text-secondary uppercase tracking-wider">Select Chit Scheme</label>
          <select
            value={schemeId}
            onChange={(e) => { setSchemeId(e.target.value); setReceiptPage(1); setDuesPage(1); }}
            className="h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
          >
            <option value="">Choose a scheme...</option>
            {schemesList.map((s) => (
              <option key={s.id} value={s.id}>{s.displayName || s.schemeNumber}</option>
            ))}
          </select>
        </div>
        {schemeId && (
          <div className="flex flex-col gap-1">
            <label className="text-caption font-bold text-text-secondary uppercase tracking-wider">Select Cycle</label>
            <select
              value={cycleNum}
              onChange={(e) => { setCycleNum(parseInt(e.target.value, 10)); setDuesPage(1); }}
              className="h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500"
            >
              {Array.from({ length: schemeDetail?.durationCycles || 1 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>Cycle {n} {n === schemeDetail?.currentCycle ? '(Current)' : ''}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Chit Receipts Log"
        subtitle="Log subscriber dues payments, generate official receipts, and manage instrument reversals."
      />
      {toolbar}
      {!schemeId ? (
        <EmptyState
          title="Select a Chit Scheme"
          description="Please choose a chit scheme from the dropdown at the top to access the receipts journal, monitor subscription dues, and record subscriber collections."
          icon={<Receipt className="w-12 h-12 text-text-muted" />}
        />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-surface-1 border border-border rounded-xl shadow-sm">
            <HeroNumber
              label={`Cycle ${cycleNum} Total Due`}
              value={summaryLoading ? '…' : formatMoney(summary?.totalDue ?? 0, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
              size="lg"
            />
            <HeroNumber
              label={`Cycle ${cycleNum} Total Collected`}
              value={summaryLoading ? '…' : formatMoney(summary?.totalCollected ?? 0, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
              size="lg"
            />
            <HeroNumber
              label={`Cycle ${cycleNum} Outstanding Accounts`}
              value={summaryLoading ? '…' : String(summary?.outstandingCount ?? 0)}
              size="lg"
            />
          </div>

          {successMessage && (
            <div className="px-4 py-3 bg-success-bg text-success-text text-sm rounded-lg border border-success-500/20">{successMessage}</div>
          )}

          <div className="flex gap-4 border-b border-border pb-px">
            <Button variant="ghost" onClick={() => setActiveTab('receipts')}
              className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'receipts' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}
            >
              Receipts Journal
            </Button>
            <Button variant="ghost" onClick={() => setActiveTab('dues')}
              className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'dues' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}
            >
              Subscription Dues (Cycle {cycleNum})
            </Button>
          </div>

          {activeTab === 'receipts' ? (
            <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
              {receiptsLoading ? (
                <Skeleton rows={5} />
              ) : receiptsError ? (
                <div className="p-8 text-center">
                  <p className="text-sm font-semibold text-danger-600">Failed to load receipts log</p>
                  <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetchReceipts()}>Retry</Button>
                </div>
              ) : (
                <table className="w-full border-collapse text-left text-sm">
                  <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                    <tr>
                      <th className="p-4">Receipt No.</th>
                      <th className="p-4">Subscriber</th>
                      <th className="p-4">Cycle</th>
                      <th className="p-4">Amount Paid</th>
                      <th className="p-4">Mode</th>
                      <th className="p-4">Receipt Date</th>
                      <th className="p-4">Status</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-text-primary">
                    {receiptsData.length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-text-muted">No receipts logged for this scheme yet.</td></tr>
                    ) : (
                      receiptsData.map(r => {
                        const info = subscriberMap.get(r.subscriberId);
                        const num = cycleNumberMap.get(r.cycleId);
                        const isCancelled = r.status === 'CANCELLED';
                        return (
                          <tr key={r.id || r.receiptNumber} className="hover:bg-surface-2 transition-colors">
                            <td className="p-4 text-xs font-mono font-semibold text-text-primary">{r.receiptNumber}</td>
                            <td className="p-4">
                              <div className="flex flex-col">
                                <span className="font-semibold text-text-primary text-sm">{info?.name || r.subscriberId.slice(0, 8)}</span>
                                {info?.tickets && <span className="text-xs text-text-muted mt-0.5">{info.tickets}</span>}
                              </div>
                            </td>
                            <td className="p-4 text-xs text-text-secondary">Cycle {num ?? '—'}</td>
                            <td className="p-4"><Money paise={r.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                            <td className="p-4 text-xs font-semibold text-text-secondary">{r.paymentMode}</td>
                            <td className="p-4 text-xs font-mono text-text-secondary">
                              {new Date(r.collectedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="p-4"><StatusBadge status={isCancelled ? 'danger' : 'success'} label={r.status} /></td>
                            <td className="p-4 text-right">
                              {!isCancelled && (
                                <Button size="sm" variant="ghost" className="text-danger-500 hover:text-danger-700 font-semibold"
                                  onClick={() => setCancelTarget({ receiptId: r.id, receiptNumber: r.receiptNumber, isSameDay: isSameDayReceipt(r.collectedAt) })}
                                >
                                  Cancel
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
              <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-t border-border text-sm text-text-muted">
                <span>Page {receiptPage}</span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" disabled={receiptPage <= 1} onClick={() => setReceiptPage(p => p - 1)}>Previous</Button>
                  <Button variant="secondary" size="sm" disabled={receiptsTotal <= receiptPage * 25} onClick={() => setReceiptPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="flex gap-1 p-1 bg-surface-2 rounded-lg w-fit border border-border">
                  {DUES_STATUS_FILTERS.map((f) => (
                    <Button key={f.value} onClick={() => { setDuesStatusFilter(f.value); setDuesPage(1); }}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${duesStatusFilter === f.value ? 'bg-surface-1 text-text-primary shadow-sm border border-border' : 'text-text-muted hover:text-text-primary bg-transparent border-transparent'}`}
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
                {duesLoading ? (
                  <Skeleton rows={5} />
                ) : duesError ? (
                  <div className="p-8 text-center">
                    <p className="text-sm font-semibold text-danger-600">Failed to load subscription dues</p>
                    <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetchDues()}>Retry</Button>
                  </div>
                ) : (
                  <table className="w-full border-collapse text-left text-sm">
                    <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                      <tr>
                        <th className="p-4">Subscriber</th>
                        <th className="p-4">Net due</th>
                        <th className="p-4">Paid</th>
                        <th className="p-4">Remaining</th>
                        <th className="p-4">Status</th>
                        <th className="p-4">Last receipt</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border text-text-primary">
                      {duesData.length === 0 ? (
                        <tr><td colSpan={7} className="p-8 text-center text-text-muted">
                          {duesStatusFilter ? `No ${duesStatusFilter.toLowerCase()} dues matching filter for cycle ${cycleNum}.` : `No dues generated for cycle ${cycleNum} of this scheme yet.`}
                        </td></tr>
                      ) : (
                        duesData.map(due => {
                          const info = subscriberMap.get(due.subscriberId);
                          const remaining = due.netDue - due.paidAmount;
                          const canPay = ['PENDING', 'PARTIAL', 'OVERDUE'].includes(due.status);
                          return (
                            <tr key={due.subscriberId} className="hover:bg-surface-2 transition-colors">
                              <td className="p-4">
                                <div className="flex flex-col">
                                  <span className="font-semibold text-text-primary text-sm">{info?.name || due.subscriberId.slice(0, 8)}</span>
                                  {info?.tickets && <span className="text-xs text-text-muted mt-0.5">{info.tickets}</span>}
                                </div>
                              </td>
                              <td className="p-4 font-mono"><Money paise={due.netDue} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                              <td className="p-4 font-mono"><Money paise={due.paidAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                              <td className="p-4 font-mono"><Money paise={remaining} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                              <td className="p-4"><StatusBadge status={dueStatusVariant(due.status)} label={due.status} /></td>
                              <td className="p-4">
                                {due.lastReceipt ? <span className="text-xs font-mono text-text-secondary">{due.lastReceipt.receiptNumber}</span> : <span className="text-text-muted">—</span>}
                              </td>
                              <td className="p-4 text-right">
                                <div className="flex items-center gap-2 justify-end">
                                  {canPay ? (
                                    <Button size="sm" variant="primary" onClick={() => setRecordPaymentDue(due)}>Record Payment</Button>
                                  ) : <span className="text-xs text-text-muted font-medium">Fully Paid</span>}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
                <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-t border-border text-sm text-text-muted">
                  <span>Page {duesPage}</span>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" disabled={duesPage <= 1} onClick={() => setDuesPage(p => p - 1)}>Previous</Button>
                    <Button variant="secondary" size="sm" disabled={duesTotal <= duesPage * 25} onClick={() => setDuesPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {recordPaymentDue && (
            <RecordPaymentModal
              due={recordPaymentDue}
              open={!!recordPaymentDue}
              onClose={() => setRecordPaymentDue(null)}
              onSuccess={(receiptNumber) => {
                setRecordPaymentDue(null);
                setSuccessMessage(`Payment recorded successfully! Receipt ${receiptNumber} generated.`);
                setTimeout(() => setSuccessMessage(''), 6000);
              }}
            />
          )}
          {cancelTarget && (
            <CancelReceiptModal
              receiptId={cancelTarget.receiptId}
              receiptNumber={cancelTarget.receiptNumber}
              isSameDay={cancelTarget.isSameDay}
              open={!!cancelTarget}
              onClose={() => setCancelTarget(null)}
              onSuccess={() => {
                setCancelTarget(null);
                setSuccessMessage(
                  cancelTarget.isSameDay
                    ? `Receipt ${cancelTarget.receiptNumber} has been successfully cancelled and the corresponding due reverted.`
                    : `Cancellation request for receipt ${cancelTarget.receiptNumber} submitted for approval.`,
                );
                setTimeout(() => setSuccessMessage(''), 6000);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
